import { useCallback, useMemo, useState } from 'react';
import { buscarLotes, SITUACOES_DO_LOTE } from '../../api/lotes.js';
import type { FiltroDeLotes, Lote, SituacaoDoLote } from '../../api/lotes.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { formatarDataCivil, formatarDataHora } from '../../componentes/datas.js';
import { filialDoFiltro, rotuloDaFilial } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import {
  CampoBusca,
  CampoFilial,
  ContadorRegistros,
  FiltroSelecao,
} from '../../componentes/Filtros.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';
import { IconeArmazem } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { formatarQuantidade } from '../movimentacoes/quantidade.js';
import { useValorAdiado } from '../movimentacoes/useValorAdiado.js';
import { QrDoLote } from './QrDoLote.js';

/**
 * `ativos` é o padrão da API e por isso NÃO vai na query: um middleware que
 * ainda não tem o filtro recusaria o parâmetro, e a aba nem abriria.
 */
const SITUACAO_PADRAO: SituacaoDoLote = 'ativos';

const OPCOES_DE_SITUACAO: readonly OpcaoFiltro[] = [
  { valor: 'ativos', rotulo: 'Ativos' },
  { valor: 'foraDeUso', rotulo: 'Fora de uso' },
  { valor: 'todos', rotulo: 'Situação · todos' },
];

function situacaoDoSeletor(valor: string): SituacaoDoLote | null {
  const situacao = SITUACOES_DO_LOTE.find((opcao) => opcao === valor);
  return situacao === undefined || situacao === SITUACAO_PADRAO ? null : situacao;
}

const COLUNAS: readonly Coluna<Lote>[] = [
  {
    chave: 'local',
    rotulo: 'Filial · depósito',
    largura: '14%',
    celula: (lote) => (
      <span className="sincronizacao__pilha">
        <span>{rotuloDaFilial(lote.codFil)}</span>
        <span className="sincronizacao__discreto mono">{lote.codDep}</span>
      </span>
    ),
  },
  {
    chave: 'produto',
    rotulo: 'Produto',
    largura: '28%',
    celula: (lote) => (
      <span className="sincronizacao__pilha">
        <span className="sincronizacao__codigo">{lote.codPro}</span>
        <span className="sincronizacao__discreto">{lote.descricaoProduto ?? '—'}</span>
      </span>
    ),
  },
  {
    chave: 'derivacao',
    rotulo: 'Derivação',
    largura: '110px',
    celula: (lote) =>
      lote.codDer === '' ? (
        <span className="sincronizacao__discreto">Sem derivação</span>
      ) : (
        <span className="mono">{lote.codDer}</span>
      ),
  },
  {
    chave: 'lote',
    rotulo: 'Lote',
    largura: '16%',
    celula: (lote) => <span className="sincronizacao__codigo">{lote.codLot}</span>,
  },
  {
    chave: 'validade',
    rotulo: 'Validade',
    largura: '110px',
    celula: (lote) =>
      lote.datVlt === null ? (
        <span className="sincronizacao__discreto">Sem validade</span>
      ) : (
        <span className="mono">{formatarDataCivil(lote.datVlt)}</span>
      ),
  },
  {
    chave: 'quantidade',
    rotulo: 'Quantidade',
    largura: '110px',
    celula: (lote) => <span className="mono">{formatarQuantidade(lote.quantidadeEstoque)}</span>,
  },
  {
    // `ativo` e `sincronizadoEm` só vêm do middleware que já tem o filtro de
    // situação; sem eles a célula fica vazia em vez de chutar "Ativo".
    chave: 'situacao',
    rotulo: 'Situação',
    largura: '150px',
    celula: (lote) => (
      <span className="sincronizacao__pilha">
        {lote.ativo !== undefined &&
          (lote.ativo ? (
            <ChipStatus tom="concluida" rotulo="Ativo" tamanho="tabela" />
          ) : (
            <ChipStatus tom="desativado" rotulo="Fora de uso" tamanho="tabela" />
          ))}
        {lote.sincronizadoEm !== undefined && (
          <span className="sincronizacao__discreto">
            Lido em {formatarDataHora(lote.sincronizadoEm)}
          </span>
        )}
      </span>
    ),
  },
];

/** Só para quem imprime etiqueta — a mesma permissão de `GET /lotes/qrcode`. */
const COLUNA_DO_QR: Coluna<Lote> = {
  chave: 'qr',
  rotulo: 'Etiqueta',
  largura: '120px',
  celula: (lote) => <QrDoLote lote={lote} />,
};

/**
 * Os lotes de PRODUTO (`saldo_lote`) que a carga de lotes gravou do ERP — um
 * por filial + depósito + produto + derivação + lote, com a validade e a
 * quantidade. Não confundir com "Lotes do ERP", os pacotes da exportação.
 */
export function AbaLotesDeProduto({
  podeImprimirEtiqueta = false,
}: {
  /** `etiquetas.imprimir`, decidido por quem monta a aba. */
  readonly podeImprimirEtiqueta?: boolean;
}) {
  const [busca, definirBusca] = useState('');
  const [filial, definirFilial] = useState('');
  const [situacao, definirSituacao] = useState<string>(SITUACAO_PADRAO);
  const [pagina, definirPagina] = useState(1);
  const buscaAdiada = useValorAdiado(busca);
  const codFil = filialDoFiltro(filial);
  const colunas = podeImprimirEtiqueta ? [...COLUNAS, COLUNA_DO_QR] : COLUNAS;

  const filtro = useMemo<FiltroDeLotes>(
    () => ({
      pagina,
      ...seInformado('busca', buscaAdiada),
      ...seInformado('codFil', codFil),
      ...seInformado('situacao', situacaoDoSeletor(situacao)),
    }),
    [pagina, buscaAdiada, codFil, situacao],
  );

  const buscar = useCallback(() => buscarLotes(filtro), [filtro]);
  const consulta = useConsulta(buscar);

  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="sincronizacao__aba">
      <FaixaDeFiltros rotulo="Filtros dos lotes de produto">
        <CampoBusca
          rotulo="Buscar por produto ou lote"
          valor={busca}
          largura={240}
          aoMudar={(valor) => {
            recortar(() => {
              definirBusca(valor);
            });
          }}
        />
        <CampoFilial
          valor={filial}
          aoMudar={(valor) => {
            recortar(() => {
              definirFilial(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Situação"
          valor={situacao}
          opcoes={OPCOES_DE_SITUACAO}
          aoMudar={(valor) => {
            recortar(() => {
              definirSituacao(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'lote', 'lotes')}
          </ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando os lotes de produto…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeArmazem tamanho={22} />}
            titulo="Nenhum lote de produto neste recorte."
            detalhe="Os lotes chegam do ERP pela carga de lotes, só os que têm saldo."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={colunas}
            chaveDe={(lote) =>
              JSON.stringify([lote.codFil, lote.codDep, lote.codPro, lote.codDer, lote.codLot])
            }
            descricao="Lotes de produto"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'lote', 'lotes')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
